import type { Exercise } from "./schemas/exercise.js";
import type { EquipmentKind } from "./schemas/muscle.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import {
  resolveExerciseMuscleCredits,
  resolveExerciseMuscleWeights,
  type AggregatorExerciseRow,
} from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// AP-9, second half (volumeLandmarks.ts carries the first). Given the muscles
// `musclesBelowMev` already found under MEV, this module answers the next
// question: WHICH catalog exercises would close each gap, and with HOW MANY
// SETS. It never touches the athlete's log or DDB — the caller hands in the
// catalog, exactly like `hardSetsByMuscle` does, so this stays a pure
// function testable with a handful of literal Exercise rows.
//
// ⛔ Sets only, never reps. This module lives on the same hard-set axis as
// `volumeLandmarks.ts` (§16.3: never mix axes) — but the deeper reason there
// is no `reps` field anywhere in this output is simpler: nothing in the repo
// sources a rep count. It isn't in the athlete's data (SessionResult persists
// no target rep count per exercise, only the top set — see the header of
// `weeklyTargetActual.ts`), it isn't in the RP landmark band (`mev`/`mav`/
// `mrv` are set counts, not rep ranges), and no rep-range table exists
// anywhere in `packages/shared`. Inventing a number here would be a constant
// with no source, which measurement-contract §17b.2 forbids outright. So the
// proposal stops at "how many SETS" and leaves rep/load prescription to
// whatever surface eventually consumes this (not built here, not this slice).
//
// ⛔ Also NOT `setProposal.ts` — that module progresses a historical anchor
// on an exercise the athlete (or plan) already picked. This module picks the
// exercise in the first place, from a volume gap, never from an anchor.

/** One catalog exercise that can close (part of) a muscle's gap. */
export interface GapExerciseCandidate {
  slug: string;
  /** Raw recruitment weight for the target muscle (`muscleContrib`), the
   *  ranking key. Unrounded, unlike `creditPerSet`. */
  weight: number;
  /** Credit this exercise earns for the target muscle per hard set, from
   *  `resolveExerciseMuscleCredits` (AP-6: full credit ≥ HARD_SET_MUSCLE_
   *  CONTRIB_MIN, fractional synergist credit below it). */
  creditPerSet: number;
  /** Sets needed to close THIS muscle's gap alone, assuming every set of this
   *  exercise and nothing else: ceil(gap / creditPerSet). Not a prescription
   *  across multiple exercises — a caller combining candidates still has to
   *  divide the gap itself. */
  setsToClose: number;
  /** Other muscles from the SAME gap list (the `gaps` this call was given,
   *  not the whole catalog) that this exercise also trains, each with its raw
   *  weight, heaviest first. The weight travels with the muscle so a 0.2
   *  synergist never reads like a full set. */
  alsoCovers: GapAlsoCovers[];
}

export interface GapAlsoCovers {
  muscle: CatalogMuscleSlug;
  weight: number;
}

/** The candidates for one muscle's gap, gap carried through for display. */
export interface VolumeGapProposal {
  muscle: CatalogMuscleSlug;
  gap: number;
  candidates: GapExerciseCandidate[];
}

/** Candidates per muscle the Progress list shows. */
export const GAP_LIST_MAX_PER_MUSCLE = 3;

// Catalog rows this proposal may ever suggest.
//
// CARDIO is out, and for the band's own reason (volumeLandmarks.ts header,
// measurement-contract §16.11): MEV/MAV/MRV is defined for resistance work, so
// a run has no value on this scale. It is excluded upstream anyway — a cardio
// bout leaves `setMetrics` with 0 volume and 0 TUT, so `countsFromMetrics`
// reads false and it never reached a hard-set count to begin with.
//
// ⛔ TIME-BASED work is NOT excluded, and the symmetry is the point.
// `countsFromMetrics` is `volumeKg > 0 || tutSec > 0` (setMetrics.ts:107): a
// plank, a carry, a Copenhagen hold all post TUT, so they DO count toward the
// weekly hard sets this band is scored against. Filtering them out of the
// proposal while they feed the actuals would mean a hold raises your adductor
// count but is never offered to close an adductor gap — and holds are exactly
// what serves the thin-coverage muscles (adductors, abductors, obliques).
//
// `archived` rows are catalog junk kept only so historical logs still resolve
// a name (exercise.ts) — never offered going forward.
//
// Physiotherapy and rehab rows are out too, by the catalog's own tags (the
// 2026-06-05 physio batch wrote them): the catalog credits Banded Ankle
// Dorsiflexion a full calf set, and on the real catalog those rows topped the
// calf list. They stay loggable and they still count; they are just never the
// answer to "how do I train this muscle more".
//
// What this gate CANNOT see: `LogSet.kind`. `p1rm` and `loaded-carry` also
// leave setMetrics at 0/0 (`setVolumeKg` gates on weighted/bodyweight, TUT only
// on the timed branch), so sets logged that way would not count either — but
// `kind` belongs to the logged SET, not to the catalog row, so no filter here
// could act on it. Nothing in `apps/` writes either kind today, so the reach is
// zero; if one ever ships, the asymmetry is upstream in setMetrics, not here.
const REHAB_TAGS: ReadonlySet<string> = new Set(["physio", "rehab"]);

// AP-9c: a row with no `muscleContrib` is out too. The recruitment fallback
// (primary[0] at 1.0, volumeByMuscle.ts) gave warm-ups and stretches full
// credit, so arm circles topped the shoulder list.
const isEligible = (
  ex: Exercise,
  isAvailable: ((ex: Exercise) => boolean) | undefined,
): boolean => {
  if (ex.archived === true) return false;
  if (!ex.muscleContrib || Object.keys(ex.muscleContrib).length === 0) return false;
  if (ex.movementPattern === "cardio") return false;
  if (ex.tags?.some((tag) => REHAB_TAGS.has(tag))) return false;
  if (isAvailable && !isAvailable(ex)) return false;
  return true;
};

// AP-9c: gear whose load can grow set after set. A band and pure bodyweight
// cannot, so at equal recruitment they come after.
const LOADABLE_GEAR: ReadonlySet<EquipmentKind> = new Set<EquipmentKind>([
  "barbell",
  "dumbbell",
  "kettlebell",
  "machine",
  "cable",
]);

/** 0 = loadable gear, 1 = anything else. */
const loadabilityRank = (ex: Exercise): number =>
  ex.equipment !== undefined && LOADABLE_GEAR.has(ex.equipment) ? 0 : 1;

/** Propose exercises + set counts to close each muscle's MEV gap.
 *
 *  For every gap, every eligible catalog exercise is read through the SAME
 *  recruitment resolution the hard-set actuals use (`volumeByMuscle.ts`, the
 *  repo's only `muscleContrib` parser, never a hand-rolled reparse): the
 *  credit gives `setsToClose`, so the number matches what the row will count;
 *  the raw weight gives the order. An exercise that does not train the target
 *  muscle is not a candidate at all; a muscle with no such exercise in the
 *  catalog gets back an explicit empty `candidates` list (the third state:
 *  unknown is never a fabricated candidate). */
export const proposeForVolumeGaps = (opts: {
  gaps: ReadonlyArray<{
    muscle: CatalogMuscleSlug;
    weeklySets: number;
    gap: number;
  }>;
  catalog: ReadonlyArray<Exercise>;
  /** Max candidates per muscle. Default GAP_LIST_MAX_PER_MUSCLE (3): enough
   *  to compare without overwhelming a coaching surface. */
  maxPerMuscle?: number;
  /** Optional: the athlete's declared-unavailable exercises, filtered out
   *  (locationUnavailable.ts's declared-absence model — never inferred). */
  isAvailable?: (ex: Exercise) => boolean;
}): VolumeGapProposal[] => {
  const { gaps, catalog, maxPerMuscle = GAP_LIST_MAX_PER_MUSCLE, isAvailable } =
    opts;

  const gapMuscles = new Set<CatalogMuscleSlug>(gaps.map((g) => g.muscle));
  const eligible = catalog.filter((ex) => isEligible(ex, isAvailable));

  // Resolve each eligible exercise ONCE, narrowed to the muscles this call's
  // gap list cares about: that narrowing is what makes `alsoCovers` mean
  // "other gaps in THIS list", not "every muscle the exercise trains".
  type Resolved = {
    credit: Map<CatalogMuscleSlug, number>;
    weight: Map<CatalogMuscleSlug, number>;
  };
  const bySlug = new Map<string, Resolved>();
  for (const ex of eligible) {
    const row: AggregatorExerciseRow = ex;
    const weight = new Map<CatalogMuscleSlug, number>();
    for (const [muscle, w] of resolveExerciseMuscleWeights(row)) {
      if (gapMuscles.has(muscle)) weight.set(muscle, w);
    }
    if (weight.size === 0) continue;
    const credit = new Map<CatalogMuscleSlug, number>();
    for (const [muscle, c] of resolveExerciseMuscleCredits(
      row,
      HARD_SET_MUSCLE_CONTRIB_MIN,
    )) {
      if (weight.has(muscle)) credit.set(muscle, c);
    }
    bySlug.set(ex.slug, { credit, weight });
  }
  const loadRank = new Map(eligible.map((ex) => [ex.slug, loadabilityRank(ex)]));

  return gaps.map((g) => {
    const candidates: GapExerciseCandidate[] = [];
    for (const ex of eligible) {
      const r = bySlug.get(ex.slug);
      if (!r) continue; // exercise trains none of this call's gap muscles
      const creditPerSet = r.credit.get(g.muscle);
      const weight = r.weight.get(g.muscle);
      // no candidate, not a fallback
      if (creditPerSet === undefined || weight === undefined) continue;
      const alsoCovers = [...r.weight]
        .filter(([m]) => m !== g.muscle)
        .map(([muscle, w]) => ({ muscle, weight: w }))
        .sort((a, b) => b.weight - a.weight || a.muscle.localeCompare(b.muscle));
      candidates.push({
        slug: ex.slug,
        weight,
        creditPerSet,
        setsToClose: Math.ceil(g.gap / creditPerSet),
        alsoCovers,
      });
    }
    // The muscle's heaviest mover first: the credit ties a 0.5 with a 1.0,
    // which put Box Jump above every calf raise. Then the exercise that also
    // trains more of the other open gaps; then slug, for determinism (same
    // pattern as rankSwapCandidates). AP-9c: the exercise that takes a
    // growing load goes before the one that covers more gaps.
    candidates.sort(
      (a, b) =>
        b.weight - a.weight ||
        (loadRank.get(a.slug) ?? 1) - (loadRank.get(b.slug) ?? 1) ||
        b.alsoCovers.length - a.alsoCovers.length ||
        a.slug.localeCompare(b.slug),
    );
    return {
      muscle: g.muscle,
      gap: g.gap,
      candidates: candidates.slice(0, Math.max(0, maxPerMuscle)),
    };
  });
};
